<template>
  <div>
    <NuxtPwaManifest />
    <NuxtLoadingIndicator
      color="#ff7600"
      :height="4"
      data-allow-mismatch="class"
    />
    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>
    <client-only>
      <AsyncGoogleOneTap v-if="!error" />
      <LazyPwaUpdateBanner />
      <NuxtSnackbar>
        <template #message-icon>
          <span />
        </template>
        <template #message-close-icon>
          <span />
        </template>
        <template #message-content="{ text }">
          <div class="vue3-snackbar-message-title" v-html="text" />
        </template>
      </NuxtSnackbar>
    </client-only>
  </div>
</template>

<script setup lang="ts">
import { defineAsyncComponent } from 'vue'
import { hydrateOnIdle } from 'vue'
import { getStoredToasts, clearStoredToasts } from '~/utility/toastStorage'
import sendDeviceInfoToSpeedcurveDefinition from '~/utility/sendDeviceInfoToSpeedcurveDefinition'

const AsyncGoogleOneTap = defineAsyncComponent({
  loader: () => import('./components/login/GoogleOneTap.vue'),
  hydrate: hydrateOnIdle(10000),
})

const error = useError()
const runtimeConfig = useRuntimeConfig()
const { i18n, t } = useI18n()
const currentLocale = i18n.locale
const route = useRoute()
const lang = (useRoute().params.lang as string) || 'en-gb'
const pinia = usePinia()
const facetsStore = useFacetsStore(pinia)
const currencyStore = useCurrencyStore(pinia)
const searchStore = useSearchStoreSetup(pinia)
const appStore = useAppStore(pinia)
useAuthStore(pinia) // Initialize auth store early
const snackbar = process.client ? useSnackbar() : null
const { hasCustomHreflangs } = useCustomHreflangs()

// load the global data needed for the app
try {
  await Promise.all([facetsStore.getFacetGroups(lang), currencyStore.load()])
} catch (error) {
  // Log error but don't block app initialization
  console.error('Failed to load global data:', error)
  // Currency store will gracefully fall back to original prices if not loaded
}

onMounted(async () => {
  await searchStore.initializeMapState()

  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- TODO: Add proper LUX type definitions
  if (window.LUX && window.LUX.markLoadTime) {
    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- LUX is a third-party SpeedCurve object
      const LUX = window.LUX
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- LUX API
      LUX.markLoadTime()
      const bundleSize = performance
        .getEntriesByType('resource')
        .filter(
          (e) => e.initiatorType === 'script' && e.name.includes('/_nuxt/'),
        )
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- PerformanceResourceTiming has decodedBodySize
        .reduce((sum, { decodedBodySize = 0 }) => sum + decodedBodySize, 0)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- LUX API
      LUX.addData('Alfred | Version', runtimeConfig.public.alfredVersion)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- LUX API
      LUX.addData('Alfred | Bundle Size', bundleSize)
    } catch (e) {
      console.error(e)
    }
  }

  // Check for any stored toast messages and display them
  if (snackbar) {
    const storedToasts = getStoredToasts()
    if (storedToasts.length > 0) {
      storedToasts.forEach((toast) => {
        snackbar.add({
          text: toast.message,
          type: toast.type,
          duration: toast.duration || 5000,
        })
      })
      // Clear the stored toasts after displaying
      clearStoredToasts()
    }
  }
})

const basePath = computed(() =>
  lang ? route.path.replace(`/${lang}/`, '/') : route.path,
)

const head = computed(() => {
  const metaObject = {
    htmlAttrs: {
      lang: currentLocale || 'en-gb',
    },
    link: [] as Array<{ rel: string; hreflang?: string; href: string }>,
    meta: [] as Array<{ property: string; content: string }>,
    script: [sendDeviceInfoToSpeedcurveDefinition],
  }

  // Canonical
  if (runtimeConfig.public?.envUrl && route.path) {
    metaObject.link.push({
      rel: 'canonical',
      href: `https://${runtimeConfig.public.envUrl}${route.path}`,
    })
  }

  // Add hreflang links only if no custom hreflangs are provided
  const hreflangs = new Set()
  if (
    !hasCustomHreflangs.value &&
    i18n.locales &&
    runtimeConfig.public?.envUrl
  ) {
    i18n.locales.forEach((locale) => {
      if (!locale?.code) return

      const addHreflang = (hreflang: string, href: string) => {
        if (!hreflangs.has(hreflang) && hreflang && href) {
          hreflangs.add(hreflang)
          metaObject.link.push({ rel: 'alternate', hreflang, href })
        }
      }

      if (locale.code === 'en-gb') {
        ;['en-gb', 'en', 'x-default'].forEach((l) => {
          addHreflang(
            l,
            `https://${runtimeConfig.public.envUrl}${basePath.value}`,
          )
        })
      } else {
        addHreflang(
          locale.code,
          `https://${runtimeConfig.public.envUrl}/${locale.code}${basePath.value}`,
        )
        if (locale.alternateHreflang) {
          addHreflang(
            locale.alternateHreflang,
            `https://${runtimeConfig.public.envUrl}/${locale.code}${basePath.value}`,
          )
        }
      }
    })
  }

  // Add og:locale meta tags
  if (currentLocale) {
    metaObject.meta.push({
      property: 'og:locale',
      content: String(currentLocale).replace(/-/g, '_'),
    })
  }

  // Add alternate og:locale tags
  if (i18n.locales && currentLocale) {
    i18n.locales
      .filter((locale) => locale?.code && locale.code !== currentLocale)
      .forEach((locale) => {
        metaObject.meta.push({
          property: 'og:locale:alternate',
          content: String(locale.code).replace(/-/g, '_'),
        })
      })
  }

  return metaObject
})

useHead(head)

// Apply meta overrides with high priority to ensure they win over page-level meta
const { metaOverride } = usePageMetaOverride()

const metaOverrideHead = computed(() => {
  if (!metaOverride.value) return {}

  const override: {
    title?: string
    titleTemplate?: string | ((title?: string) => string)
    meta?: Array<{
      hid: string
      name?: string
      property?: string
      content: string
      tagPriority: number
    }>
  } = {}

  // Override title if provided
  // CRITICAL: Use titleTemplate to force override since title doesn't support tagPriority
  if (metaOverride.value.title) {
    override.title = metaOverride.value.title
    // Force the title by using a template that ignores page title
    override.titleTemplate = () => metaOverride.value!.title!
  }

  // Override meta description if provided
  if (metaOverride.value.description) {
    override.meta = [
      {
        hid: 'description',
        name: 'description',
        content: metaOverride.value.description,
        // High priority to override page-level meta
        tagPriority: 100,
      },
      {
        hid: 'og:description',
        property: 'og:description',
        content: metaOverride.value.description,
        tagPriority: 100,
      },
    ]
  }

  // Note: H1 cannot be overridden automatically here since it's in page templates
  // Pages should use the composable to access metaOverride.h1 if they want to use it

  return override
})

// Apply with high priority on both server and client for proper SSR
useHead(metaOverrideHead)

// Watch for any network errors and display a message
if (process.client) {
  watch(
    () => appStore.hasAnyNetworkError,
    (hasError) => {
      if (!hasError && snackbar) {
        snackbar.clear()
        return
      }

      if (snackbar) {
        snackbar.add({
          text: t('Network connection lost'),
          type: 'error',
          dismissible: false,
        })
      }
    },
  )
}
</script>
